/**
 * /my-coaching/business/lead-magnets/leads — the visitors who shared their details on
 * the coach's Wheel of Life page, most recent activity first, with where
 * each stands (the coach's follow-up status), a filter on it and a CSV export.
 * Only this coach's leads.
 *
 * Aliases: coach leads tab, leads list, export leads, lead status filter
 */

import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { PageSection } from "~/components/ui/page-section";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { exportMyCoachLeadsCsvFn } from "~/lib/functions/coach-leads";
import { myCoachLeadsQueryOptions } from "~/lib/queries/coachLeadsQuery";
import {
  type CoachLeadStatus,
  type CoachLeadSummary,
} from "~/lib/shared/coach-leads";

import {
  LeadStatusChip,
  LeadStatusFilter,
} from "../../-components/lead-status";
import { formatLeadTime } from "../../-components/lead-time";

export const Route = createFileRoute(
  "/_user/my-coaching/business/lead-magnets/leads/",
)({
  staticData: { title: "Leads" },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(myCoachLeadsQueryOptions());
  },
  component: LeadsPage,
});

function downloadCsv(csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `wheel-of-life-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function LeadsPage(): React.JSX.Element {
  const { data: leads } = useSuspenseQuery(myCoachLeadsQueryOptions());
  const [filter, setFilter] = useState<CoachLeadStatus | null>(null);
  const shown = filter ? leads.filter((lead) => lead.status === filter) : leads;
  const exportCsv = useMutation({
    mutationFn: () => exportMyCoachLeadsCsvFn(),
    onSuccess: downloadCsv,
    onError: () => {
      toast.error("We couldn't export your leads. Please try again.");
    },
  });

  return (
    <PageSection
      title={`Leads · ${leads.length}`}
      action={
        leads.length > 0 && (
          <Button
            type="button"
            size="sm"
            loading={exportCsv.isPending}
            onClick={() => exportCsv.mutate()}
          >
            Export CSV
          </Button>
        )
      }
    >
      {leads.length === 0 ? (
        <Card className="p-0">
          <EmptyState
            icon={Users}
            size="sm"
            title="No leads yet"
            description="Leads appear here when visitors share their results on your Wheel of Life page."
          />
        </Card>
      ) : (
        <>
          <LeadStatusFilter leads={leads} value={filter} onChange={setFilter} />
          {shown.length === 0 ? (
            <Card className="p-0">
              <EmptyState
                icon={Users}
                size="sm"
                title="No leads with this status"
                description="Change the filter to see your other leads."
              />
            </Card>
          ) : (
            <Card className="gap-0 overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Assessment</TableHead>
                    <TableHead>Follow-up</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Last activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.map((lead) => (
                    <LeadRow key={lead.id} lead={lead} />
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}

      <p className="text-text-secondary text-sm">
        Leads are people who shared their results with you on your Wheel of Life
        page. You send any follow-up yourself, so you are responsible for it
        under email and privacy law.
      </p>
    </PageSection>
  );
}

function LeadRow({ lead }: { lead: CoachLeadSummary }): React.JSX.Element {
  return (
    <TableRow>
      <TableCell className="font-medium">
        <Link
          to="/my-coaching/business/lead-magnets/leads/$leadId"
          params={{ leadId: lead.id }}
          className="text-purple-700 underline-offset-4 hover:underline"
        >
          {lead.firstName}
        </Link>
      </TableCell>
      <TableCell className="text-text-secondary">{lead.email}</TableCell>
      <TableCell>
        <LeadStatus completedCount={lead.completedCount} />
      </TableCell>
      <TableCell>
        <LeadStatusChip status={lead.status} />
      </TableCell>
      <TableCell className="text-text-secondary">
        {lead.latestShape ?? "—"}
      </TableCell>
      <TableCell className="text-text-secondary">
        {lead.joined ? "Joined DCM" : "Not yet"}
      </TableCell>
      <TableCell className="text-text-secondary whitespace-nowrap">
        {formatLeadTime(lead.lastActivityAt)}
      </TableCell>
    </TableRow>
  );
}

function LeadStatus({
  completedCount,
}: {
  completedCount: number;
}): React.JSX.Element {
  if (completedCount === 0) {
    return (
      <Badge variant="pending" size="sm" radius="full">
        Started
      </Badge>
    );
  }
  return (
    <Badge variant="success" size="sm" radius="full">
      {completedCount > 1 ? `Completed · ${completedCount}` : "Completed"}
    </Badge>
  );
}
